<script>
  import Fa from "svelte-fa"
  import {
    faUtensils,
    faFireBurner,
    faHourglassEnd,
    faPeopleGroup,
    faFire,
    faListOl,
    faArrowUpRightFromSquare
  } from "@fortawesome/free-solid-svg-icons"

  import {
    Lightbox,
    LightboxGallery,
    GalleryThumbnail,
    GalleryImage
  } from "svelte-lightbox"

  export let initialData

  let { recipe } = initialData
  let { name, links, gallery, ingredients, instructions, cooking_info } = recipe

  function getDisplayUrl(url) {
    try {
      return new URL(url).hostname.replace(/^www\./, "")
    } catch {
      return url
    }
  }

  const arrowsConfig = {
    galleryArrowsCharacter: "hide",
    enableKeyboardControl: true
  }
</script>

<main class="flex flex-col md:flex-row md:space-x-10">
  <div class="flex flex-col md:w-9/12 md:gap-2">
    <!-- Recipe name and links -->
    <div class="flex flex-col md:flex-row gap-2 md:items-center">
      <div class="grow">
        <div class="recipe-name">
          {name}
        </div>
      </div>
    </div>

    <!-- Cooking info -->
    {#if cooking_info && ((cooking_info.prep_time && cooking_info.prep_time !== "?") || (cooking_info.cook_time && cooking_info.cook_time !== "?") || (cooking_info.total_time && cooking_info.total_time !== "?") || cooking_info.yield || cooking_info.calories)}
      <div
        class="flex flex-wrap items-center gap-x-10 gap-y-2 text-sm border-b border-t
        border-border py-1"
      >
        {#if cooking_info.prep_time && cooking_info.prep_time !== "?"}
          <div class="flex items-center gap-1.5">
            <Fa icon={faUtensils} class="text-xs text-fg-muted" />
            <span class="text-fg-muted">Prep</span>
            <span class="text-fg">{cooking_info.prep_time}</span>
          </div>
        {/if}

        {#if cooking_info.cook_time && cooking_info.cook_time !== "?"}
          <div class="flex items-center gap-1.5">
            <Fa icon={faFireBurner} class="text-xs text-fg-muted" />
            <span class="text-fg-muted">Cook</span>
            <span class="text-fg">{cooking_info.cook_time}</span>
          </div>
        {/if}

        {#if cooking_info.total_time && cooking_info.total_time !== "?"}
          <div class="flex items-center gap-1.5">
            <Fa icon={faHourglassEnd} class="text-xs text-fg-muted" />
            <span class="text-fg-muted">Total</span>
            <span class="text-fg">{cooking_info.total_time}</span>
          </div>
        {/if}

        {#if cooking_info.yield}
          <div class="flex items-center gap-1.5">
            <Fa icon={faPeopleGroup} class="text-xs text-fg-muted" />
            <span class="text-fg-muted">Servings</span>
            <span class="text-fg">{cooking_info.yield}</span>
          </div>
        {/if}

        {#if cooking_info.calories}
          <div class="flex items-center gap-1.5">
            <Fa icon={faFire} class="text-xs text-fg-muted" />
            <span class="text-fg-muted">Cal/serv</span>
            <span class="text-fg">{cooking_info.calories}</span>
          </div>
        {/if}
      </div>
    {/if}

    <!-- Links -->
    {#if links && links.length > 0}
      <div class="mt-2 flex flex-wrap items-center gap-2 text-sm">
        {#each links as link}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate/5 border border-slate/20 hover:border-accent/50 hover:bg-accent/5 rounded-xl text-fg-muted hover:text-accent transition-colors"
            data-controller="tooltip"
            data-tooltip-content-value={link.url}
          >
            <Fa icon={faArrowUpRightFromSquare} class="text-xs opacity-50" />
            <span class="truncate max-w-48"
              >{link.title || getDisplayUrl(link.url)}</span
            >
          </a>
        {/each}
      </div>
    {/if}

    <!-- Ingredients and Instructions -->
    <div
      class="flex flex-col mt-4 md:flex-row space-y-10 md:space-y-0 md:space-x-10"
    >
      <!-- Ingredients -->
      <div class="w-full md:w-96">
        <div class="flex flex-col gap-4">
          <div
            class="flex flex-row border-b border-dashed border-slate pb-2 items-center"
          >
            <div class="grow text-lg font-light thick">Ingredients</div>
          </div>

          {#if ingredients}
            {#each ingredients as group}
              <div class="border-l-4 border-accent pl-4">
                {#if ingredients.length > 1 && group.name}
                  <div class="mb-2 font-body font-bold">
                    {group.name}
                  </div>
                {/if}

                {#if group.ingredients}
                  <div class="flex flex-col gap-1">
                    {#each group.ingredients as ingredient}
                      <div class="font-light text-lg md:text-base py-0 px-1">
                        {ingredient.name}
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      </div>

      <!-- Instructions -->
      <div class="w-full">
        <div class="flex flex-col gap-4">
          <div
            class="flex flex-row border-b border-dashed border-slate pb-2 items-center"
          >
            <div class="grow text-lg font-light thick">Instructions</div>
          </div>

          {#if instructions}
            {#each instructions as group}
              <div class="border-l-4 border-primary pl-4">
                {#if instructions.length > 1 && group.name}
                  <div class="mb-2 flex flex-row items-center relative">
                    <div class="mr-0 absolute">
                      <Fa icon={faListOl} />
                    </div>
                    <div class="font-body font-bold thick pl-6 tracking-wide">
                      {group.name}
                    </div>
                  </div>
                {/if}

                {#if group.steps}
                  <div class="flex flex-col gap-1">
                    {#each group.steps as step, i}
                      <div class="flex flex-row items-start content-start">
                        <div
                          class="pt-0 md:pt-1 mr-2 md:mr-1 text-base md:text-sm"
                        >
                          {i + 1}.
                        </div>
                        <div
                          class="text-lg md:text-base py-0.5 px-2 leading-tight"
                        >
                          {@html step.step}
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      </div>
    </div>
  </div>

  <!-- Gallery sidebar -->
  <div class="mt-6 md:mt-0 md:w-3/12">
    {#if gallery && gallery.length > 0}
      <LightboxGallery {arrowsConfig}>
        <!-- Layout-->
        <svelte:fragment slot="thumbnail">
          {#each gallery as { thumbnail }, id}
            <div class="py-2">
              <GalleryThumbnail {id}>
                <img
                  class="block object-cover object-center w-full max-h-40 rounded-lg hover:cursor-pointer"
                  src={thumbnail}
                  alt="Recipe"
                />
              </GalleryThumbnail>
            </div>
          {/each}
        </svelte:fragment>

        {#each gallery as { type, src }}
          <GalleryImage>
            {#if type == "image"}
              <img {src} alt="Recipe" />
            {:else if type == "video"}
              <video {src} controls autoplay>
                <track kind="captions" />
              </video>
            {:else}
              <p>Unknown content type {type}</p>
            {/if}
          </GalleryImage>
        {/each}
      </LightboxGallery>
    {/if}
  </div>
</main>

<style>
  .recipe-name {
    @apply text-2xl py-1 px-2 md:-mx-2 text-fg leading-tight md:mr-8;
  }
</style>
